"use client";

import { useCallback, useMemo } from "react";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type ConstitutionLabels,
  constitutionIsReady,
  CONSTITUTION_FIELDS as F,
  formatConstitution,
  STATE_CONSTITUTIONS,
} from "@/lib/tool-kit/legal-sources/constitution-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const CONSTITUTION_CITATION_GENERATOR_STRINGS = {
  formHeading: "Provision details",
  resultsHeading: "Your citations",
  emptyPrompt:
    "Choose the constitution and enter an article or amendment number to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  jurisdictionLabel: "Constitution",
  usConstitution: "U.S. Constitution",
  stateConstitution: "{state} Constitution",
  stateNames: {
    al: "Alabama",
    ak: "Alaska",
    az: "Arizona",
    ar: "Arkansas",
    ca: "California",
    co: "Colorado",
    ct: "Connecticut",
    de: "Delaware",
    fl: "Florida",
    ga: "Georgia",
    hi: "Hawaii",
    id: "Idaho",
    il: "Illinois",
    in: "Indiana",
    ia: "Iowa",
    ks: "Kansas",
    ky: "Kentucky",
    la: "Louisiana",
    me: "Maine",
    md: "Maryland",
    ma: "Massachusetts",
    mi: "Michigan",
    mn: "Minnesota",
    ms: "Mississippi",
    mo: "Missouri",
    mt: "Montana",
    ne: "Nebraska",
    nv: "Nevada",
    nh: "New Hampshire",
    nj: "New Jersey",
    nm: "New Mexico",
    ny: "New York",
    nc: "North Carolina",
    nd: "North Dakota",
    oh: "Ohio",
    ok: "Oklahoma",
    or: "Oregon",
    pa: "Pennsylvania",
    ri: "Rhode Island",
    sc: "South Carolina",
    sd: "South Dakota",
    tn: "Tennessee",
    tx: "Texas",
    ut: "Utah",
    vt: "Vermont",
    va: "Virginia",
    wa: "Washington",
    wv: "West Virginia",
    wi: "Wisconsin",
    wy: "Wyoming",
  },
  partLabel: "What you are citing",
  partArticle: "An article",
  partAmendment: "An amendment",
  partPreamble: "The preamble",
  partWhole: "The whole constitution",
  numberLabel: "Article or amendment number",
  numberPlaceholder: "I or 1",
  numberHint:
    "Type 1 or I — U.S. articles and amendments, and state articles, become Roman numerals.",
  sectionLabel: "Section (optional)",
  sectionPlaceholder: "8",
  clauseLabel: "Clause (optional)",
  clausePlaceholder: "3",
  statusLabel: "Status",
  statusInForce: "In force",
  statusRepealed: "Repealed",
  statusAmended: "Amended or superseded",
  statusYearLabel: "Year repealed or amended",
  statusYearPlaceholder: "1933",
  statusYearHint:
    "Only for a provision no longer in force, e.g. 1933 for the 18th Amendment.",
  versionTitleLabel: "Title of the copy you read (for MLA)",
  versionTitlePlaceholder:
    "The Constitution of the United States: A Transcription",
  websiteLabel: "Website (for MLA)",
  websitePlaceholder: "National Archives",
  publisherLabel: "Publisher (for MLA)",
  publisherPlaceholder: "U.S. National Archives and Records Administration",
  dateLabel: "Date published or updated (for MLA)",
  datePlaceholder: "YYYY-MM-DD",
  urlLabel: "URL (for MLA)",
  urlPlaceholder:
    "https://www.archives.gov/founding-docs/constitution-transcript",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes)",
  bluebookLabel: "Bluebook (22nd edition)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  citation: "Citation",
  apaNote:
    "APA 7 (§11.9) gives a provision in force no date; a repealed one takes the year of repeal. U.S. article and amendment numbers are Roman numerals.",
  apaWholeNote:
    "APA 7 gives the Constitution as a whole no reference-list entry — name it in your sentence (the U.S. Constitution). Choose an article or amendment to get a reference entry.",
  mlaNote:
    "MLA cites the copy you read, not the canonical text. Name the article and section in your sentence or in the parenthesis, as shown.",
  mlaNeedsVersion:
    "MLA cites the version you consulted. Add its title — for example, the National Archives transcription — to build the Works Cited entry.",
  chicagoNote:
    "Chicago follows the Bluebook for constitutions and cites them in notes only — they are not listed in the bibliography.",
  bluebookNote:
    "In law-review footnotes, set “U.S. Const.” in small capitals. Rule 11 allows no short form other than id.",
  legalWholeNote:
    "A passing mention of the Constitution needs no note — cite the article or amendment you rely on.",
  example: {
    jurisdiction: "us",
    part: "article",
    number: "1",
    section: "8",
    clause: "3",
    status: "inForce",
    versionTitle: "The Constitution of the United States: A Transcription",
    website: "National Archives",
    publisher: "U.S. National Archives and Records Administration",
    date: "2026-09-08",
    url: "https://www.archives.gov/founding-docs/constitution-transcript",
  },
} as const;

type Strings = typeof CONSTITUTION_CITATION_GENERATOR_STRINGS;

function buildFields(strings: Strings): SourceField[] {
  const stateNames = strings.stateNames as Record<string, string>;
  const jurisdictions = [
    { value: "us", label: strings.usConstitution },
    ...Object.keys(STATE_CONSTITUTIONS).map((code) => ({
      value: code,
      label: t(strings.stateConstitution, { state: stateNames[code] ?? code }),
    })),
  ];
  return [
    {
      id: F.jurisdiction,
      kind: "select",
      label: strings.jurisdictionLabel,
      options: jurisdictions,
    },
    {
      id: F.part,
      kind: "select",
      label: strings.partLabel,
      options: [
        { value: "article", label: strings.partArticle },
        { value: "amendment", label: strings.partAmendment },
        { value: "preamble", label: strings.partPreamble },
        { value: "whole", label: strings.partWhole },
      ],
    },
    {
      id: F.number,
      kind: "text",
      label: strings.numberLabel,
      placeholder: strings.numberPlaceholder,
      hint: strings.numberHint,
    },
    {
      id: F.section,
      kind: "text",
      label: strings.sectionLabel,
      placeholder: strings.sectionPlaceholder,
    },
    {
      id: F.clause,
      kind: "text",
      label: strings.clauseLabel,
      placeholder: strings.clausePlaceholder,
    },
    {
      id: F.status,
      kind: "select",
      label: strings.statusLabel,
      options: [
        { value: "inForce", label: strings.statusInForce },
        { value: "repealed", label: strings.statusRepealed },
        { value: "amended", label: strings.statusAmended },
      ],
    },
    {
      id: F.statusYear,
      kind: "text",
      label: strings.statusYearLabel,
      placeholder: strings.statusYearPlaceholder,
      hint: strings.statusYearHint,
    },
    {
      id: F.versionTitle,
      kind: "text",
      label: strings.versionTitleLabel,
      placeholder: strings.versionTitlePlaceholder,
      wide: true,
    },
    {
      id: F.website,
      kind: "text",
      label: strings.websiteLabel,
      placeholder: strings.websitePlaceholder,
    },
    {
      id: F.publisher,
      kind: "text",
      label: strings.publisherLabel,
      placeholder: strings.publisherPlaceholder,
    },
    {
      id: F.date,
      kind: "date",
      label: strings.dateLabel,
      placeholder: strings.datePlaceholder,
    },
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function constitutionLabels(strings: Strings): ConstitutionLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    bluebook: strings.bluebookLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    citation: strings.citation,
    apaNote: strings.apaNote,
    apaWholeNote: strings.apaWholeNote,
    mlaNote: strings.mlaNote,
    mlaNeedsVersion: strings.mlaNeedsVersion,
    chicagoNote: strings.chicagoNote,
    bluebookNote: strings.bluebookNote,
    legalWholeNote: strings.legalWholeNote,
  };
}

export default function ConstitutionCitationGeneratorWidget() {
  const strings = useToolUi(CONSTITUTION_CITATION_GENERATOR_STRINGS);
  const fields = useMemo(() => buildFields(strings), [strings]);
  const labels = useMemo(() => constitutionLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatConstitution(values, labels),
    [labels]
  );

  return (
    <SourceCitationBuilder
      example={strings.example}
      fields={fields}
      format={format}
      isReady={constitutionIsReady}
      strings={strings}
    />
  );
}
